import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {catchError, Observable, throwError} from 'rxjs';
import {ClientData} from './client.model';
import {GridGetService, GridServiceParams, GridServiceResponse} from 'src/app/shared/model/page-grid';
import {removeEmptyParams} from 'src/infrastructure/helper/remove-empty-params';
import {getAutocompleteValue} from 'src/infrastructure/helper/autocomplete-helper';
import {MatSnackBar} from '@angular/material/snack-bar';

@Injectable({
  providedIn: 'root'
})
export class ClientGridService implements GridGetService<ClientData> {
  private readonly baseUrl = 'dbov2/client/search';
  private readonly http = inject(HttpClient);
  private readonly _snackBar = inject(MatSnackBar);
  
  private readonly MAX_SAFE_PAGE_SIZE = 100000;

  getItems({filters, page, sort} : GridServiceParams): Observable<GridServiceResponse<ClientData>>  {
    const pageSize = page?.pageSize && page.pageSize > this.MAX_SAFE_PAGE_SIZE 
      ? this.MAX_SAFE_PAGE_SIZE 
      : (page?.pageSize || 25);
      
    if (page?.pageSize && page.pageSize > this.MAX_SAFE_PAGE_SIZE) {
      if (page.pageSize !== 1000000) {
        this._snackBar.open(
          `Request size limited to ${this.MAX_SAFE_PAGE_SIZE.toLocaleString()} records. Results may be truncated.`, 
          'Dismiss',
          { duration: 5000 }
        );
      }
    }

    const params:Record<string, string | number | boolean> = {
      pageSize: pageSize,
      pageNumber: (page?.pageIndex || 0) + 1,
      propertyName: sort?.active || '',
      orderKey: sort?.direction || '',

      clientName: getAutocompleteValue(filters?.['clientName']),
      agentName: getAutocompleteValue(filters?.['agentName']),
      dataSource: filters?.['dataSource'] ?? '',
    };

    removeEmptyParams(params);
    
    return this.http.get<GridServiceResponse<ClientData>>(`${this.baseUrl}`, { params: {...params} })
    .pipe(catchError((error:HttpErrorResponse)=>{
        if (error.status === 413 || error.status === 504) {
          this._snackBar.open(
            'The request was too large. Try using filters to reduce the data size.',
            'Dismiss',
            { duration: 8000 }
          );
        }
        return throwError(() => new Error(error?.message || 'Server error'));
    }));
  }
}